/**
 * usePublicTheme — Applies the shared marketing-site theme to the
 * Owntune configurator's anonymous flows (/l/:slug, /campaigns/:slug).
 *
 * Behaviour:
 *   - Adds `owntune-theme` class to `<html>` on mount, removes on unmount.
 *     This scopes the marketing tokens (defined in
 *     `@owntune/theme/theme.css`) to the current page only; the
 *     authenticated admin app is unaffected once the visitor navigates
 *     away.
 *   - Resolves the active theme with priority:
 *       1. `?theme=dark|light` URL param (writes result back to
 *          localStorage so the visitor's next visit inherits it)
 *       2. `localStorage["owntune-theme"]` (shared key with marketing
 *          site — if both apps run on the same domain the visitor's
 *          marketing-site choice is honoured immediately)
 *       3. Default: `dark`
 *   - Toggles `light` class alongside `owntune-theme` on `<html>`
 *     (never adds `dark` — dark is the default when only
 *     `owntune-theme` is present).
 *
 * DOES NOT read `prefers-color-scheme`: the marketing site does not,
 * so honouring the OS preference here would diverge from the
 * marketing site's own default (always dark).
 */
import { useEffect } from 'react';

const HTML_CLASS = 'owntune-theme';
const LIGHT_CLASS = 'light';
const STORAGE_KEY = 'owntune-theme';

function readInitialTheme() {
    try {
        const params = new URLSearchParams(window.location.search);
        const themeParam = (params.get('theme') || '').toLowerCase();
        if (themeParam === 'dark' || themeParam === 'light') {
            try { localStorage.setItem(STORAGE_KEY, themeParam); } catch { /* ignore */ }
            return themeParam;
        }
        const stored = (localStorage.getItem(STORAGE_KEY) || '').toLowerCase();
        if (stored === 'dark' || stored === 'light') return stored;
    } catch { /* ignore */ }
    return 'dark';
}

export function usePublicTheme() {
    useEffect(() => {
        const root = document.documentElement;
        const hadThemeClass = root.classList.contains(HTML_CLASS);
        const hadLightClass = root.classList.contains(LIGHT_CLASS);

        const theme = readInitialTheme();

        root.classList.add(HTML_CLASS);
        if (theme === 'light') root.classList.add(LIGHT_CLASS);
        else root.classList.remove(LIGHT_CLASS);

        return () => {
            // Restore original class state so the theme does not leak
            // into the authenticated app shell after navigation.
            if (!hadThemeClass) root.classList.remove(HTML_CLASS);
            if (hadLightClass) root.classList.add(LIGHT_CLASS);
            else root.classList.remove(LIGHT_CLASS);
        };
    }, []);
}
